import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { gameAPI, postsAPI } from "../utils/api";
import { toast } from "react-toastify";
import { useGameSocket } from "./useGameSocket";

export const useGameManager = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // State
  const [socket, setSocket] = useState(null);
  const [gameState, setGameState] = useState("menu"); // menu, lobby, task, results
  const [gameCode, setGameCode] = useState("");
  const [nickname, setNickname] = useState(user?.username || "");
  const [maxPlayers, setMaxPlayers] = useState(10);
  const [gameMode, setGameMode] = useState("classic");
  const [timeLimit, setTimeLimit] = useState(30); // Default 30 mins for Art Jam
  const [drawTime, setDrawTime] = useState(90); // Default 90s per drawing round
  const [currentGame, setCurrentGame] = useState(null);
  const [currentRound, setCurrentRound] = useState(0);
  const [promptText, setPromptText] = useState("");
  const [drawings, setDrawings] = useState([]);
  const [chains, setChains] = useState([]); // Gartic Phone chains for results

  // Task State
  const [currentTask, setCurrentTask] = useState(null); // {chainId, taskType, previousEntry}
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [submittedCount, setSubmittedCount] = useState(0); // Track how many players have submitted
  const [totalPlayers, setTotalPlayers] = useState(0); // Total players in game

  // Reveal State
  const [currentRevealChain, setCurrentRevealChain] = useState(0);
  const [currentRevealStep, setCurrentRevealStep] = useState(0);
  const [isRevealing, setIsRevealing] = useState(false);

  // Transition State
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [nextRoundNumber, setNextRoundNumber] = useState(0);

  // Refs
  const currentGameRef = useRef(null);
  const nicknameRef = useRef(nickname);
  const hasJoinedRoom = useRef(false);
  const shouldSaveToStorage = useRef(true);
  const hasLeftGame = useRef(false);

  // Keep refs in sync
  useEffect(() => {
    currentGameRef.current = currentGame;
  }, [currentGame]);

  useEffect(() => {
    nicknameRef.current = nickname;
  }, [nickname]);

  // Initialize Socket
  useGameSocket({
    setSocket,
    currentGameRef,
    nicknameRef,
    setCurrentGame,
    setGameState,
    setCurrentRound,
    setHasSubmitted,
    setSubmittedCount,
    setCurrentTask,
    setTotalPlayers,
    setPromptText,
    setChains,
    setCurrentRevealChain,
    setCurrentRevealStep,
    setIsRevealing,
    setIsTransitioning,
    setNextRoundNumber,
  });

  // Load game state from localStorage on mount OR rejoin from URL
  useEffect(() => {
    // Check for rejoin parameter (returning from sketchbook)
    const params = new URLSearchParams(location.search);
    const rejoinCode = params.get("code");
    const isRejoin = params.get("rejoin") === "true";

    if (isRejoin && rejoinCode) {
      console.log("[REJOIN] Rejoining game:", rejoinCode);

      // Get saved game state
      const savedGameState = localStorage.getItem("arthive_game_state");
      if (savedGameState) {
        try {
          const { gameCode: savedCode, nickname: savedNickname } =
            JSON.parse(savedGameState);

          if (savedCode === rejoinCode.toUpperCase()) {
            setGameCode(savedCode);
            setNickname(savedNickname);

            // Fetch current game state
            gameAPI
              .getGame(savedCode)
              .then(({ data }) => {
                setCurrentGame(data);

                if (data.status === "in-progress") {
                  setCurrentRound(data.currentRound);
                  setGameState("task");

                  // Get task to show UI and check submission status
                  gameAPI
                    .getPlayerTask(savedCode, savedNickname)
                    .then(({ data: task }) => {
                      setCurrentTask(task);
                      setHasSubmitted(task.alreadySubmitted || false);
                    });
                } else if (data.status === "waiting") {
                  setGameState("lobby");
                } else if (data.status === "finished") {
                  setGameState("results");
                  gameAPI.getResults(savedCode).then(({ data: results }) => {
                    setChains(results.chains);
                  });
                }

                shouldSaveToStorage.current = true;
              })
              .catch((error) => {
                console.error("[REJOIN] Failed to fetch game:", error);
                localStorage.removeItem("arthive_game_state");
                toast.error("Failed to rejoin game");
              });

            return;
          }
        } catch (error) {
          console.error("[REJOIN] Error parsing saved state:", error);
        }
      }
    }

    // Don't restore if we just left
    if (hasLeftGame.current) {
      hasLeftGame.current = false;
      return;
    }

    const savedGameState = localStorage.getItem("arthive_game_state");
    if (savedGameState) {
      try {
        const { gameCode: savedCode, nickname: savedNickname } =
          JSON.parse(savedGameState);

        if (savedCode && savedNickname) {
          // Try to rejoin the game
          setGameCode(savedCode);
          setNickname(savedNickname);

          gameAPI
            .getGame(savedCode)
            .then(({ data }) => {
              // Check if the player is still in the game
              const playerStillInGame = data.players.some(
                (player) => player.nickname === savedNickname,
              );

              if (playerStillInGame) {
                setCurrentGame(data);
                shouldSaveToStorage.current = true;

                // Determine state based on backend status
                if (data.status === "waiting") {
                  setGameState("lobby");
                } else if (data.status === "in-progress") {
                  setGameState("task");
                  setCurrentRound(data.currentRound);
                  // Fetch task
                  gameAPI
                    .getPlayerTask(savedCode, savedNickname)
                    .then(({ data: task }) => {
                      setCurrentTask(task);
                      setHasSubmitted(task.alreadySubmitted || false);
                    })
                    .catch(console.error);
                } else if (data.status === "finished") {
                  setGameState("results");
                  gameAPI.getResults(savedCode).then(({ data: results }) => {
                    setChains(results.chains);
                  });
                }
              } else {
                // Player was removed, clear the saved state and don't restore
                localStorage.removeItem("arthive_game_state");
                shouldSaveToStorage.current = false;
              }
            })
            .catch(() => {
              localStorage.removeItem("arthive_game_state");
              shouldSaveToStorage.current = false;
            });
        }
      } catch (error) {
        localStorage.removeItem("arthive_game_state");
        shouldSaveToStorage.current = false;
      }
    }
  }, [location.search, nickname]);

  // Save game state to localStorage
  useEffect(() => {
    if (
      currentGame &&
      (gameState === "lobby" ||
        gameState === "task" ||
        gameState === "results") &&
      shouldSaveToStorage.current
    ) {
      localStorage.setItem(
        "arthive_game_state",
        JSON.stringify({
          gameCode: currentGame.code,
          nickname,
          gameState,
        }),
      );
    } else if (gameState === "menu") {
      localStorage.removeItem("arthive_game_state");
      shouldSaveToStorage.current = true; // Reset for next time
    }
  }, [currentGame, gameState, nickname]);

  // Join socket room when entering lobby
  useEffect(() => {
    if (
      socket &&
      currentGame &&
      gameState === "lobby" &&
      !hasJoinedRoom.current
    ) {
      console.log("Joining game room:", currentGame.code);
      socket.emit("join-game", { code: currentGame.code, nickname });
      hasJoinedRoom.current = true;
    } else if (gameState !== "lobby") {
      hasJoinedRoom.current = false;
    }
  }, [socket, currentGame, gameState, nickname]);

  // Poll for game updates in lobby
  useEffect(() => {
    let pollInterval;
    if (gameState === "lobby" && currentGame) {
      pollInterval = setInterval(async () => {
        try {
          const { data: updatedGame } = await gameAPI.getGame(currentGame.code);
          setCurrentGame(updatedGame);
          // Check if game has started
          if (
            updatedGame.status === "in-progress" &&
            updatedGame.currentRound > 0 &&
            gameState === "lobby"
          ) {
            clearInterval(pollInterval);
            // Fetch first task
            const { data: task } = await gameAPI.getPlayerTask(
              updatedGame.code,
              nickname,
            );
            setCurrentTask(task);
            setHasSubmitted(task.alreadySubmitted || false);
            setCurrentRound(updatedGame.currentRound);
            setGameState("task");
            toast.info("Game started!");
          }
        } catch (error) {
          console.error("Failed to poll game:", error);
        }
      }, 2000); // Poll every 2 seconds
    }
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [gameState, currentGame, nickname]);

  // Actions
  const createGame = async () => {
    if (!nickname.trim()) {
      toast.error("Please enter a nickname");
      return;
    }

    try {
      const { data } = await gameAPI.createGame(
        nickname,
        3,
        maxPlayers,
        gameMode,
        timeLimit,
        drawTime,
      );
      setCurrentGame(data);
      setGameCode(data.code);
      setGameState("lobby");
      toast.success(`Game created! Code: ${data.code}`);
    } catch (error) {
      toast.error("Failed to create game");
    }
  };

  const joinGame = async () => {
    if (!nickname.trim()) {
      toast.error("Please enter a nickname");
      return;
    }

    if (!gameCode.trim()) {
      toast.error("Please enter a game code");
      return;
    }

    try {
      const { data } = await gameAPI.joinGame(
        gameCode.toUpperCase(),
        nickname,
        user?._id,
      );
      setCurrentGame(data);
      setGameState("lobby");
      toast.success("Joined game successfully!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to join game");
    }
  };

  const startGame = async () => {
    if (!currentGame) return;

    console.log("[START GAME] Starting game with code:", currentGame.code);

    try {
      // Update game status in database
      const response = await gameAPI.startGame(currentGame.code);

      // Update local state immediately
      const updatedGame = response.data.gameSession;
      setCurrentGame(updatedGame);
      setCurrentRound(1);
      setHasSubmitted(false);
      setSubmittedCount(0);
      setTotalPlayers(updatedGame.players.length);

      // Get first task
      const { data: task } = await gameAPI.getPlayerTask(
        updatedGame.code,
        nickname,
      );
      setCurrentTask(task);
      setHasSubmitted(task.alreadySubmitted || false);
      setGameState("task");

      // Notify other players via socket
      if (socket) {
        socket.emit("start-game", { code: updatedGame.code });
      }

      toast.success("Game started!");
    } catch (error) {
      toast.error("Failed to start game");
      console.error("[START GAME] Error:", error);
    }
  };

  const leaveGame = async () => {
    if (!currentGame) return;

    const codeToLeave = currentGame.code;
    const nicknameLeaving = nickname;

    // Mark that we've left to prevent rejoining on refresh
    hasLeftGame.current = true;

    // Immediately clear localStorage and prevent saving
    localStorage.removeItem("arthive_game_state");
    shouldSaveToStorage.current = false;

    // Reset state immediately
    setCurrentGame(null);
    setGameCode("");
    setGameState("menu");
    hasJoinedRoom.current = false;

    try {
      // Leave socket room first
      if (socket) {
        socket.emit("leave-game", {
          code: codeToLeave,
          nickname: nicknameLeaving,
        });
      }

      // Call backend to remove player from game
      await gameAPI.leaveGame(codeToLeave, nicknameLeaving);

      toast.info("Left the game");
    } catch (error) {
      console.error("Error leaving game:", error);
      toast.info("Left the game");
    }
  };

  const submitTask = async (drawingData = null, isAutoSubmit = false) => {
    if (!currentTask) {
      toast.error("No task available");
      return;
    }

    let data;
    const taskType = currentTask.taskType;

    // Validate and get data
    if (taskType === "prompt") {
      if (!promptText.trim() && !isAutoSubmit) {
        toast.error("Please enter a prompt or guess");
        return;
      }
      data = promptText.trim() || "Time's up!";
    } else if (taskType === "drawing") {
      if (drawingData) {
        data = drawingData; // This is now a File object
      } else if (isAutoSubmit) {
        // Create a blank canvas blob for auto-submit
        const canvas = document.createElement("canvas");
        canvas.width = 800;
        canvas.height = 600;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, 800, 600);
        ctx.font = "48px sans-serif";
        ctx.fillStyle = "#000000";
        ctx.textAlign = "center";
        ctx.fillText("Time's up!", 400, 300);

        data = await new Promise((resolve) =>
          canvas.toBlob(resolve, "image/png"),
        );
      } else {
        // Drawings are submitted from the drawing canvas, not here
        toast.error("Please use the drawing canvas to submit drawings");
        return;
      }
    }

    try {
      const formData = new FormData();
      formData.append("playerNickname", nickname);
      formData.append("chainId", currentTask.chainId);
      formData.append("type", taskType);

      if (taskType === "drawing") {
        formData.append("image", data);
      } else {
        formData.append("data", data);
      }

      const response = await gameAPI.submitEntry(currentGame.code, formData);

      setHasSubmitted(true);
      setPromptText("");

      // Update submission tracking
      setSubmittedCount(response.data.submittedCount || 0);
      setTotalPlayers(
        response.data.totalPlayers || currentGame?.players?.length || 0,
      );

      // Notify via socket
      if (socket) {
        socket.emit("entry-submitted", {
          code: currentGame.code,
          nickname,
          round: currentRound,
        });
      }

      toast.success(
        taskType === "prompt" ? "Prompt submitted!" : "Drawing submitted!",
      );

      // Check if game ended or round advanced
      if (response.data.gameSession.status === "finished") {
        setGameState("results");
        setCurrentGame(response.data.gameSession);
        // Fetch results immediately
        gameAPI.getResults(currentGame.code).then(({ data: results }) => {
          setChains(results.chains);
        });
      } else if (response.data.allSubmitted) {
        // All players submitted, move to next round
        setCurrentRound(response.data.gameSession.currentRound);
        setSubmittedCount(0); // Reset counter for new round
        // Fetch next task
        const { data: nextTask } = await gameAPI.getPlayerTask(
          currentGame.code,
          nickname,
        );
        setCurrentTask(nextTask);
        setHasSubmitted(nextTask.alreadySubmitted || false);
        toast.info(`Round ${response.data.gameSession.currentRound} started!`);
      }
    } catch (error) {
      console.error("[SUBMIT] Error:", error);
      toast.error(
        error.response?.data?.message || "Failed to submit. Please try again.",
      );
    }
  };

  const repostToFeed = async (drawingUrl) => {
    if (!isAuthenticated) {
      toast.error("Please login to repost artwork");
      navigate("/login");
      return;
    }

    try {
      await postsAPI.createPost({
        title: `Game Art - ${currentGame.code}`,
        imageUrl: drawingUrl,
        caption: `Created in ArtHive Game - Code: ${currentGame.code}`,
        isGameArt: true,
        gameSessionId: currentGame._id,
      });

      toast.success("Posted to your feed!");
    } catch (error) {
      toast.error("Failed to post artwork");
    }
  };

  const revealNext = () => {
    if (!currentGame || !chains || chains.length === 0) return;

    const isHost = nickname === currentGame.hostId;
    if (!isHost) {
      toast.error("Only the host can control reveals");
      return;
    }

    const currentChain = chains[currentRevealChain];
    if (!currentChain) return;

    // Show original prompt first, then each entry
    const totalSteps = currentChain.entries.length + 1; // +1 for original prompt

    if (currentRevealStep < totalSteps - 1) {
      // Move to next step in current chain
      const nextStep = currentRevealStep + 1;
      setCurrentRevealStep(nextStep);
      setIsRevealing(true);

      if (socket) {
        socket.emit("reveal-step", {
          code: currentGame.code,
          chainIndex: currentRevealChain,
          stepIndex: nextStep,
        });
      }
    } else if (currentRevealChain < chains.length - 1) {
      // Move to next chain
      const nextChain = currentRevealChain + 1;
      setCurrentRevealChain(nextChain);
      setCurrentRevealStep(0);
      setIsRevealing(true);

      if (socket) {
        socket.emit("reveal-step", {
          code: currentGame.code,
          chainIndex: nextChain,
          stepIndex: 0,
        });
      }
    } else {
      // All done
      toast.info("All chains revealed!");
    }
  };

  const revealReset = () => {
    const isHost = nickname === currentGame.hostId;
    if (!isHost) {
      toast.error("Only the host can control reveals");
      return;
    }

    setCurrentRevealChain(0);
    setCurrentRevealStep(0);
    setIsRevealing(false);

    if (socket) {
      socket.emit("reveal-reset", { code: currentGame.code });
    }
  };

  const playAgain = async () => {
    if (!currentGame) return;
    try {
      const { data } = await gameAPI.createGame(
        nickname,
        3,
        currentGame.maxPlayers,
        currentGame.gameMode,
        currentGame.timeLimit,
        currentGame.drawTime,
      );
      if (socket) {
        socket.emit("play-again", {
          oldCode: currentGame.code,
          newCode: data.code,
        });
      }
      setCurrentGame(data);
      setGameCode(data.code);
      setDrawings([]);
      setChains([]);
      setCurrentRound(0);
      setCurrentTask(null);
      setHasSubmitted(false);
      setCurrentRevealChain(0);
      setCurrentRevealStep(0);
      setIsRevealing(false);
      setIsTransitioning(false);
      setGameState("lobby");
    } catch {
      toast.error("Failed to start new game");
    }
  };

  return {
    // State
    gameState,
    setGameState,
    gameCode,
    setGameCode,
    nickname,
    setNickname,
    maxPlayers,
    setMaxPlayers,
    gameMode,
    setGameMode,
    timeLimit,
    setTimeLimit,
    drawTime,
    setDrawTime,
    currentGame,
    setCurrentGame,
    currentRound,
    setCurrentRound,
    promptText,
    setPromptText,
    drawings,
    setDrawings,
    chains,
    setChains,
    currentTask,
    setCurrentTask,
    hasSubmitted,
    setHasSubmitted,
    submittedCount,
    setSubmittedCount,
    totalPlayers,
    setTotalPlayers,
    currentRevealChain,
    setCurrentRevealChain,
    currentRevealStep,
    setCurrentRevealStep,
    isRevealing,
    setIsRevealing,
    isTransitioning,
    setIsTransitioning,
    nextRoundNumber,
    setNextRoundNumber,
    socket,
    user,

    // Actions
    createGame,
    joinGame,
    startGame,
    leaveGame,
    submitTask,
    repostToFeed,
    revealNext,
    revealReset,
    playAgain,
  };
};
